import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { StoreLabelKind, StoreLabelService } from '../../services/storeLabel.service';
import { LABEL_LAYOUT_OPTIONS, LabelLayout } from '../../interfaces/fixedAsset/fixedAssetCount.interface';

interface Props {
  open: boolean;
  kind: StoreLabelKind;
  ids: string[];
  onClose: () => void;
  /** Copies to start with, e.g. the quantity just received; capped at the dialog's 100. */
  defaultCopies?: number;
}

const NOUN: Record<StoreLabelKind, string> = { ITEM: 'item', LOT: 'lot', SERIAL: 'serial number' };

/** Q3: copies per label, sticker sheet and start position, then downloads the label PDF for items, lots or serials. */
const StoreLabelDialog: React.FC<Props> = ({ open, kind, ids, onClose, defaultCopies = 1 }) => {
  const toast = useToast();
  const [layout, setLayout] = useState<LabelLayout>('A4_3x7');
  const [startPosition, setStartPosition] = useState('1');
  const [copies, setCopies] = useState(String(Math.min(100, Math.max(1, Math.floor(defaultCopies)))));
  const [busy, setBusy] = useState(false);

  const option = LABEL_LAYOUT_OPTIONS.find(o => o.value === layout)!;
  const start = Number(startPosition);
  const startOk = Number.isInteger(start) && start >= 1 && start <= option.perPage;
  const n = Number(copies);
  const copiesOk = Number.isInteger(n) && n >= 1 && n <= 100;

  const print = async () => {
    setBusy(true);
    try {
      await StoreLabelService.print({ kind, ids, copies: n, layout, startPosition: option.perPage > 1 ? start : undefined });
      toast.showSuccess('Labels downloaded');
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to print labels');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" data-help-section="store.labels.dialog">
      <DialogTitle>Print barcode labels</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {ids.length} {NOUN[kind]}{ids.length === 1 ? '' : 's'} selected. Each label carries a barcode and a QR code.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField data-help="store.labels.field.copies" label="Copies of each label" size="small" type="number" value={copies}
            onChange={e => setCopies(e.target.value)} error={!copiesOk}
            helperText={copiesOk ? undefined : 'Enter 1 to 100'} inputProps={{ min: 1, max: 100 }} />
          <TextField select data-help="store.labels.field.layout" label="Sticker sheet" size="small" value={layout}
            onChange={e => { setLayout(e.target.value as LabelLayout); setStartPosition('1'); }}>
            {LABEL_LAYOUT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          {option.perPage > 1 && (
            <TextField data-help="store.labels.field.start" label="Start at label number" size="small" type="number" value={startPosition}
              onChange={e => setStartPosition(e.target.value)} error={!startOk}
              helperText={startOk ? `1 to ${option.perPage}. Skips stickers already used.` : `Enter 1 to ${option.perPage}`}
              inputProps={{ min: 1, max: option.perPage }} />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="store.labels.cancel" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button data-help="store.labels.download" variant="contained" onClick={print} disabled={busy || ids.length === 0 || !startOk || !copiesOk}>
          {busy ? 'Preparing...' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreLabelDialog;
